import { deriveDeviceParts } from "./deviceName";

/**
 * The optional `clientBrowser` / `clientOs` fields a PC surface sends on its own
 * device PATCH, so the phone can render a real browser mark instead of a generic
 * puzzle piece.
 */
export interface PlatformFields {
  clientBrowser?: string;
  clientOs?: string;
}

/**
 * Structured platform brands for the current surface, ready to spread into a
 * self-PATCH body.
 *
 * Both sentinels from [deriveDeviceParts] are dropped rather than transmitted:
 *
 * - `browser` is the literal string `"Browser"` when the UA is unrecognised.
 *   Sending it would persist a fake brand that is indistinguishable from a real
 *   one server-side, and the phone would render a "Browser" icon for something
 *   it simply failed to identify. Absent means unknown; a value means a brand.
 * - `os` is `""` when unrecognised, which the server's `isValidPlatformBrand`
 *   rejects outright (non-empty required) — so sending it would turn an ordinary
 *   unknown platform into a 400 on an otherwise valid rename.
 *
 * The two are dropped independently: a recognised browser on an unrecognised OS
 * still reports the browser. Callers spread the result, so an empty object
 * leaves both stored values untouched (the server's UPDATE uses COALESCE).
 */
export function platformFields(userAgent: string): PlatformFields {
  const { browser, os } = deriveDeviceParts(userAgent);
  return {
    ...(browser && browser !== "Browser" ? { clientBrowser: browser } : {}),
    ...(os ? { clientOs: os } : {}),
  };
}
